import type React from 'react';
import createApp from '@shopify/app-bridge';
import { useAppBridge } from '@shopify/app-bridge-react';
import { getSessionToken } from '@shopify/app-bridge-utils';
import { setStorage } from '../../../utils/cache';
import { setShopifyInstance } from '../../../api/axiosWrapper';

const API_KEY = import.meta.env.PUBLIC_SHOPIFY_API_KEY;
const shopifyCacheKey: string = import.meta.env.PUBLIC_SHOPIFY_CACHE_KEY;

declare global {
  interface Window {
    shopify?: unknown;
    Shopify?: unknown;
    ShopifyApp?: unknown;
    [key: string]: unknown;
  }
}

interface Props {
  children: React.ReactNode;
}

/**
 * 检测当前是否在 Shopify 管理员环境中
 * @returns {boolean} 是否在 Shopify 环境中
 */
const isShopifyEnvironment = (): boolean => {
  // 方法1: 检查 URL 参数中是否包含 shop 和 host
  const urlParams = new URLSearchParams(window.location.search);
  const hasShopParam = urlParams.has('shop');
  const hasHostParam = urlParams.has('host');
  
  // 方法2: 检查 Shopify 特定的全局对象
  const hasShopifyObject = Boolean(
    window.shopify || 
    window.Shopify || 
    window.ShopifyApp
  );
  
  // 方法3: 检查 referrer 是否来自 Shopify 域名
  const referrerFromShopify = document.referrer.includes('myshopify.com') || document.referrer.includes('shopify.com');
  
  // 方法4: 检查是否在 iframe 中，Shopify 应用通常在 iframe 中运行
  const isInIframe = window !== window.parent;
  
  // 综合判断
  return (hasShopParam && hasHostParam) || hasShopifyObject || referrerFromShopify || isInIframe;
};
 
/**
 * Shopify App Bridge 组件
 * 自动检测环境并在 Shopify 环境中连接 App Bridge
 */
function ShopifyAppBridge({ children }: Props) {
  // 检查是否在 Shopify 环境中
  const inShopifyEnvironment = isShopifyEnvironment();
  console.log('判断是否Shopify环境: inShopifyEnvironment', inShopifyEnvironment);
  
  if (inShopifyEnvironment) {
    // 在 Shopify 环境中，配置 App Bridge
    const urlParams = new URLSearchParams(window.location.search);
    const host = urlParams.get('host');
    
    if (!host) {
      console.error('未找到 host 参数，无法初始化 Shopify App Bridge');
      return <>{children}</>;
    }
    
    const config = {
      apiKey: API_KEY,
      host,
      forceRedirect: true,
      debug: import.meta.env.PUBLIC_NODE_ENV === 'development'
    };
    (window as Record<string, unknown>)[shopifyCacheKey] = config;
    
    try {
      const app = createApp(config);
      console.log('Shopify App Bridge 初始化成功', app);
      
      const shopify = useAppBridge();
      
      // 设置shopify实例到axiosWrapper中，以便API请求使用
      setShopifyInstance(shopify);
    } catch (error) {
      console.error('Shopify App Bridge 初始化失败:', error);
    }
  } else {
    console.log('非 Shopify 环境，跳过 App Bridge 初始化');
  }
  
  return (<>{children}</>);
}

export default ShopifyAppBridge;
